import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';
import RecompenseEmptyState from './RecompenseEmptyState';
import RecompenseList from './RecompenseList';
import RecompenseForm from './RecompenseForm';
import {
  PAGE_SUBTITLE,
  PAGE_TITLE,
  normalizeRecompensesListResponse,
  resolveEffectFamily,
} from '../../utils/recompensePresentation';
import {
  buildRecompenseApiPayload,
  emptyRecompenseForm,
  formFromRecompense,
  getErrorMessage,
  getErrorStatus,
  validateRecompenseForm,
  buildReorderItemsAfterMove,
  syncAutoLibelleIfNeeded,
} from '../../utils/recompenseForm';

/**
 * F2.2b / F2.3b / FIX-2 — Écran Récompenses (boutons + ConfirmDialog).
 */
export default function FideliteRecompenses({ commercantId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [recompenses, setRecompenses] = useState([]);
  const [toast, setToast] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRecompenseForm());
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDraftCta, setShowDraftCta] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    if (!commercantId) {
      setError('Identifiant commerçant manquant');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const payload = await apiService.getFideliteRecompenses(commercantId);
      setRecompenses(normalizeRecompensesListResponse(payload));
    } catch (err) {
      const status = getErrorStatus(err);
      if (status === 403) {
        setForbidden(true);
        setError(
          getErrorMessage(
            err,
            'Accès interdit : vous ne pouvez gérer que les récompenses de votre commerce'
          )
        );
      } else {
        setError(getErrorMessage(err, 'Impossible de charger les récompenses'));
      }
      setRecompenses([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    load();
  }, [load]);

  const closeForm = useCallback(() => {
    if (submitting) return;
    setFormOpen(false);
    setEditingId(null);
    setFormErrors({});
    setFormError(null);
    setShowDraftCta(false);
  }, [submitting]);

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setForm(emptyRecompenseForm({ statut: 'BROUILLON' }));
    setFormErrors({});
    setFormError(null);
    setShowDraftCta(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setEditingId(row.id);
    setForm(formFromRecompense(row));
    setFormErrors({});
    setFormError(null);
    setShowDraftCta(false);
    setFormOpen(true);
  };

  const persistPayload = async (payload) => {
    if (formMode === 'create' && !editingId) {
      return apiService.createFideliteRecompense(commercantId, payload);
    }
    const id = editingId;
    if (!id) throw new Error('Récompense introuvable');
    return apiService.updateFideliteRecompense(commercantId, id, payload);
  };

  const handleSubmit = async ({ forceStatut } = {}) => {
    if (submitting) return;
    let draftForm =
      forceStatut != null ? { ...form, statut: forceStatut } : { ...form };
    const synced = syncAutoLibelleIfNeeded(
      draftForm.libelle,
      draftForm.offerKind,
      draftForm.valeurEuros,
      draftForm.valeurPourcent
    );
    draftForm = { ...draftForm, libelle: synced.libelle };
    const validated = validateRecompenseForm(draftForm);
    if (!validated.ok) {
      setFormErrors(validated.errors);
      setFormError('Vérifiez les champs du formulaire.');
      return;
    }
    setFormErrors({});
    setFormError(null);
    setSubmitting(true);
    setBusyId('*');
    try {
      const payload = buildRecompenseApiPayload(validated.values, {
        forCreate: formMode === 'create' && !editingId,
      });
      if (forceStatut) payload.statut = forceStatut;
      await persistPayload(payload);
      showToast(
        payload.statut === 'ACTIVE'
          ? 'Récompense active.'
          : 'Récompense enregistrée.'
      );
      setShowDraftCta(false);
      setFormOpen(false);
      setEditingId(null);
      await load();
    } catch (err) {
      const status = getErrorStatus(err);
      setFormError(
        getErrorMessage(
          err,
          status === 403
            ? 'Accès interdit.'
            : status === 404
              ? 'Récompense introuvable.'
              : status === 422
                ? 'Activation impossible sur cet environnement.'
                : 'Enregistrement impossible.'
        )
      );
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  };

  const handleKeepDraft = async () => {
    await handleSubmit({ forceStatut: 'BROUILLON' });
  };

  const requestActivate = (row) => {
    if (submitting || busyId) return;
    const family = resolveEffectFamily(row);
    const natureLabel = family.effectLabel || row.libelle || 'Récompense';
    const points = Number(row.coutPoints);
    setConfirmAction({
      kind: 'activate',
      row,
      title: 'Activer cette récompense ?',
      description:
        'Elle sera immédiatement disponible pour les clients éligibles.',
      confirmLabel: 'Activer',
      tone: 'primary',
      natureLabel,
      pointsLabel: Number.isFinite(points) ? `${points} points` : null,
    });
  };

  const requestSuspend = (row) => {
    if (submitting || busyId) return;
    const family = resolveEffectFamily(row);
    const natureLabel = family.effectLabel || row.libelle || 'Récompense';
    const points = Number(row.coutPoints);
    setConfirmAction({
      kind: 'suspend',
      row,
      title: 'Suspendre cette récompense ?',
      description:
        'Elle ne sera plus proposée aux clients tant qu’elle reste suspendue.',
      confirmLabel: 'Suspendre',
      tone: 'primary',
      natureLabel,
      pointsLabel: Number.isFinite(points) ? `${points} points` : null,
    });
  };

  const requestArchive = (row) => {
    if (submitting || busyId) return;
    const family = resolveEffectFamily(row);
    const natureLabel = family.effectLabel || row.libelle || 'Récompense';
    setConfirmAction({
      kind: 'archive',
      row,
      title: 'Archiver cette récompense ?',
      description:
        'Elle disparaîtra du catalogue courant, mais restera conservée pour l’historique. Cette action n’est pas une suppression définitive, mais la récompense ne sera plus proposée aux clients.',
      confirmLabel: 'Archiver',
      tone: 'danger',
      natureLabel,
      pointsLabel: null,
      wasActive: row.statut === 'ACTIVE',
    });
  };

  const runConfirmAction = async () => {
    if (!confirmAction?.row || submitting) return false;
    const { kind, row } = confirmAction;
    setBusyId(row.id);
    setSubmitting(true);
    try {
      if (kind === 'activate') {
        await apiService.updateFideliteRecompense(commercantId, row.id, {
          statut: 'ACTIVE',
        });
        showToast('Récompense active.');
      } else if (kind === 'suspend') {
        await apiService.updateFideliteRecompense(commercantId, row.id, {
          statut: 'SUSPENDUE',
        });
        showToast('Récompense suspendue.');
      } else if (kind === 'archive') {
        await apiService.archiverFideliteRecompense(commercantId, row.id);
        showToast('Récompense archivée.');
        if (editingId === row.id) closeForm();
      }
      setConfirmAction(null);
      await load();
      return true;
    } catch (err) {
      showToast(
        getErrorMessage(
          err,
          kind === 'activate'
            ? 'Activation impossible.'
            : kind === 'suspend'
              ? 'Suspension impossible.'
              : 'Archivage impossible.'
        )
      );
      return false;
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  };

  const handleMove = async (row, direction) => {
    if (submitting || busyId) return;
    const items = buildReorderItemsAfterMove(recompenses, row.id, direction);
    if (!items) return;
    setBusyId(row.id);
    setSubmitting(true);
    try {
      await apiService.reorderFideliteRecompenses(commercantId, items);
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'Réordonnancement impossible.'));
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  };

  const readOnlyForm = form.statut === 'ARCHIVEE';

  return (
    <div className="space-y-6" data-testid="fidelite-recompenses-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{PAGE_TITLE}</h2>
          <p className="text-sm text-gray-600 mt-1">{PAGE_SUBTITLE}</p>
        </div>
        {!loading && !forbidden && !error && !formOpen && (
          <button
            type="button"
            data-testid="recompense-add"
            onClick={openCreate}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#4A148C] text-white hover:bg-[#6A1B9A]"
          >
            Ajouter une récompense
          </button>
        )}
      </div>

      <div
        className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
        data-testid="recompense-incomplete-banner"
      >
        Plusieurs récompenses (montant ou %) peuvent être <strong>actives</strong> en même
        temps. Produit / cadeau arrive bientôt.
      </div>

      {toast && (
        <div
          className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-900"
          data-testid="recompense-toast"
          role="status"
        >
          {toast}
        </div>
      )}

      {formOpen && (
        <RecompenseForm
          mode={formMode}
          form={form}
          errors={formErrors}
          submitting={submitting}
          readOnly={readOnlyForm}
          formError={formError}
          showSaveAsDraft={showDraftCta}
          onChange={(next) => {
            setForm(next);
            setFormErrors({});
          }}
          onSubmit={() => handleSubmit()}
          onSaveAsDraft={handleKeepDraft}
          onCancel={closeForm}
        />
      )}

      {loading && (
        <p className="text-sm text-gray-500" data-testid="recompense-loading">
          Chargement des récompenses…
        </p>
      )}

      {!loading && forbidden && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          data-testid="recompense-forbidden"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !forbidden && error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-2"
          data-testid="recompense-error"
          role="alert"
        >
          <p>{error}</p>
          <button
            type="button"
            onClick={load}
            className="text-sm font-medium text-[#4A148C] underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && recompenses.length === 0 && !formOpen && (
        <RecompenseEmptyState onAdd={openCreate} />
      )}

      {!loading && !error && recompenses.length > 0 && (
        <RecompenseList
          recompenses={recompenses}
          busyId={busyId}
          onEdit={openEdit}
          onActivate={requestActivate}
          onSuspend={requestSuspend}
          onArchive={requestArchive}
          onMoveUp={(row) => handleMove(row, 'up')}
          onMoveDown={(row) => handleMove(row, 'down')}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction?.title || 'Confirmer'}
        description={confirmAction?.description}
        confirmLabel={confirmAction?.confirmLabel || 'Confirmer'}
        cancelLabel="Annuler"
        tone={confirmAction?.tone || 'primary'}
        confirmDisabled={submitting}
        onConfirm={runConfirmAction}
        onCancel={() => !submitting && setConfirmAction(null)}
      >
        {confirmAction && (
          <div
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 space-y-1"
            data-testid={`recompense-confirm-${confirmAction.kind}`}
          >
            <p className="font-semibold">{confirmAction.natureLabel}</p>
            {confirmAction.pointsLabel ? (
              <p className="text-gray-600">{confirmAction.pointsLabel}</p>
            ) : null}
            {confirmAction.wasActive ? (
              <p className="text-amber-800 text-xs mt-1">
                C’est actuellement une récompense active. Après archivage, elle ne sera plus
                échangeable.
              </p>
            ) : null}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
